import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Headphones, Mail, Phone, XCircle } from "lucide-react";
import { Stars } from "@/components/support/RateChat";
import { Navbar } from "@/components/Navbar";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { ChatThread, ChatComposer } from "@/components/support/ChatParts";

const POLL_MS = 3000;

function ChatView({ chatId, L }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => api.get(`/support-chat/assigned/${chatId}`).then(({ data }) => setD(data)).catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail))), [chatId]);
  useEffect(() => { load(); const id = setInterval(load, POLL_MS); return () => clearInterval(id); }, [load]);
  if (err) return <p className="text-center text-slate-500 py-10" data-testid="assigned-chat-error">{err}</p>;
  if (!d) return <p className="text-center text-slate-400 py-10">…</p>;
  const c = d.chat; const open = c.status === "open";
  const send = async (text) => {
    const tmp = { msg_id: `tmp_${Date.now()}`, sender_role: "agent", text, created_at: new Date().toISOString(), pending: true };
    setD((x) => ({ ...x, messages: [...x.messages, tmp] }));
    try { await api.post(`/support-chat/assigned/${chatId}/messages`, { text }); load(); return true; }
    catch (e) { setD((x) => ({ ...x, messages: x.messages.filter((m) => m.msg_id !== tmp.msg_id) })); toast.error(formatApiErrorDetail(e.response?.data?.detail)); return false; }
  };
  const close = async () => {
    if (!window.confirm(L("إغلاق المحادثة؟", "Close this conversation?"))) return;
    try { await api.post(`/support-chat/assigned/${chatId}/close`); await load(); toast.success(L("تم إغلاق المحادثة", "Conversation closed")); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="bg-white rounded-3xl border border-[#EFEBE0] overflow-hidden flex flex-col h-[70vh]" data-testid="assigned-chat-view">
      <div className="px-4 py-3 border-b border-[#EFEBE0] space-y-1">
        <div className="flex items-center gap-2"><p className="font-bold flex-1" data-testid="assigned-chat-customer">{c.user_name}</p>
          {open && <button onClick={close} className="text-red-600 text-sm font-semibold flex items-center gap-1" data-testid="assigned-chat-close"><XCircle className="w-4 h-4" />{L("إغلاق المحادثة", "Close conversation")}</button>}
          {c.rating && <Stars value={c.rating} testid="assigned-chat-rating" />}</div>
        <p className="text-xs text-slate-500 flex flex-wrap gap-x-4 gap-y-1" dir="ltr">
          {c.user_email && <span className="flex items-center gap-1" data-testid="assigned-chat-email"><Mail className="w-3.5 h-3.5" />{c.user_email}</span>}
          {c.user_phone && <span className="flex items-center gap-1" data-testid="assigned-chat-phone"><Phone className="w-3.5 h-3.5" />{c.user_phone}</span>}
        </p>
      </div>
      <ChatThread messages={d.messages} customerName={c.user_name} testid="assigned-chat-thread" />
      {open ? <ChatComposer onSend={send} placeholder={L("اكتب ردك...", "Write your reply...")} testid="assigned-chat-input" />
        : <p className="text-center text-sm text-slate-500 py-3 border-t" data-testid="assigned-chat-closed">{L("تم إغلاق هذه المحادثة", "This conversation is closed")}</p>}
    </div>
  );
}

export default function AssignedSupport() {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const { lang, dir } = useLang();
  const L = (ar, en) => (lang === "en" ? en : ar);
  const Back = dir === "rtl" ? ChevronRight : ChevronLeft;
  const [list, setList] = useState(null);
  useEffect(() => {
    const pull = () => api.get("/support-chat/assigned").then(({ data }) => setList(data)).catch(() => setList([]));
    pull(); const id = setInterval(pull, 10000); return () => clearInterval(id);
  }, []);
  return (
    <div className="min-h-screen bg-[#FAF8F3]">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 py-6" data-testid="assigned-support-page">
        <button onClick={() => navigate(chatId ? "/assigned-support" : "/account")} className="text-slate-500 text-sm mb-4 flex items-center gap-1" data-testid="assigned-back"><Back className="w-4 h-4" />{L("رجوع", "Back")}</button>
        <h1 className="font-headline text-2xl font-bold mb-5">{L("محادثات دعم مسندة إليّ", "Support chats assigned to me")}</h1>
        {chatId ? <ChatView chatId={chatId} L={L} /> : !list ? <p className="text-slate-400">…</p> : !list.length
          ? <p className="text-center text-slate-500 py-10" data-testid="assigned-empty">{L("لا توجد محادثات مسندة إليك", "No chats assigned to you")}</p>
          : <div className="space-y-3" data-testid="assigned-list">{list.map((c) => (
            <button key={c.chat_id} onClick={() => navigate(`/assigned-support/${c.chat_id}`)} className="w-full text-start bg-white rounded-2xl border border-[#EFEBE0] p-4 flex items-start gap-3 hover:border-[#1A5C3A]" data-testid={`assigned-item-${c.chat_id}`}>
              <span className="w-10 h-10 rounded-full bg-[#E8EFEA] flex items-center justify-center shrink-0"><Headphones className="w-5 h-5 text-[#1A5C3A]" /></span>
              <span className="flex-1 min-w-0">
                <span className="flex items-center gap-2"><span className="font-bold truncate">{c.user_name}</span>
                  {c.unread_agent > 0 && <span className="min-w-5 h-5 px-1.5 rounded-full bg-red-600 text-white text-[11px] flex items-center justify-center">{c.unread_agent}</span>}
                  {c.status !== "open" && <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">{L("مغلقة", "Closed")}</span>}</span>
                <span className="block text-sm text-slate-600 truncate">{c.last_message}</span>
              </span>
            </button>))}</div>}
      </main>
    </div>
  );
}
